import { z } from 'zod';

export const GradingSystemSchema = z.object({
  kind: z.enum(['v-scale', 'font', 'yds', 'yds-pm', 'french-sport', 'custom']),
  grades: z.array(z.string().min(1)).min(1),
});

export const ClimbingDisciplineSchema = z.enum([
  'bouldering',
  'top_rope',
  'lead',
  'auto_belay',
]);

export const GymSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(2).max(40).regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(100),
  ownerUserId: z.string().uuid(),
  gradingSystem: GradingSystemSchema,
  timezone: z.string().min(1),
  betaPenalty: z.number().min(0).max(1),
});

export const GymCreateSchema = GymSchema.pick({
  slug: true,
  name: true,
  gradingSystem: true,
  timezone: true,
}).partial({ timezone: true });

export const RouteStatusSchema = z.enum(['active', 'archived']);

export const RouteSchema = z.object({
  id: z.string().uuid(),
  gymId: z.string().uuid(),
  name: z.string().min(1).max(100),
  holdColor: z.string().min(1).max(40),
  difficulty: z.string().min(1),
  difficultyRank: z.number().int().min(0),
  status: RouteStatusSchema,
  photoUrls: z.array(z.string().url()),
  processedPhotoUrls: z.array(z.string()),
  betaVideoUrl: z.string().url().nullable(),
  setByUserId: z.string().uuid().nullable(),
});

export const RouteCreateSchema = RouteSchema.pick({
  name: true,
  holdColor: true,
  difficulty: true,
  difficultyRank: true,
});

export const ChallengeModifierSchema = z.object({
  id: z.string().uuid(),
  gymId: z.string().uuid(),
  name: z.string().min(1).max(80),
  multiplier: z.number().min(0).max(5),
  isActive: z.boolean(),
});

export const PartyStatusSchema = z.enum(['active', 'ended']);

export const GameModeKeySchema = z.enum([
  'wheel_spin',
  'power_up',
  'board',
  'triple_choice',
  'steal',
  'streak',
  'draft',
  'marathon',
  'head_to_head',
  'team',
  'expedition',
]);

export const GameSettingsSchema = z.object({
  challengesEnabled: z.boolean(),
  betaEnabled: z.boolean(),
  // Defaults keep parties created before the balance update valid:
  // handicap scoring + comeback boost are the new baseline.
  scoringMode: z.enum(['handicap', 'absolute']).default('handicap'),
  comebackBoost: z.boolean().default(true),
  scaleToGrade: z.boolean(),
  wheelSize: z.number().int().min(3).max(10),
  modifierChance: z.number().min(0).max(1),
  maxAttemptsBeforeForceBail: z.number().int().min(1).max(50).nullable(),
  turnTimeLimitSec: z.number().int().min(10).max(600).nullable(),
  // Whole-session deadline in minutes. Optional + default for parties
  // created before this setting existed.
  gameTimeLimitMin: z.number().int().min(1).max(360).nullable().default(null),
  marathonDurationMin: z.number().int().min(1).max(180),
  stealBidWindowSec: z.number().int().min(5).max(120),
  draftStartingPoints: z.number().int().min(0).max(10_000),
  rerollEnabled: z.boolean(),
  rerollCost: z.number().int().min(0).max(10_000),
  routeTagFilter: z.array(z.string().uuid()),
  // Optional with a default so parties created before this setting
  // existed (whose stored game_settings JSON has no clusterRouting
  // key) still validate. mergeSettings fills it from DEFAULTs anyway.
  clusterRouting: z.boolean().default(true),
  // Optional + default for backward compatibility with parties created
  // before the points toggle existed. Defaults to on. The server clamps
  // it to true for modes that require a point economy.
  pointsEnabled: z.boolean().default(true),
  // Team mode bingo card size (N×N). Optional + default for older parties.
  teamBoardSize: z.number().int().min(3).max(6).default(5),
  // Board mode map. Optional + default for parties created before maps.
  boardMap: z
    .enum(['summit_trail', 'crux_cavern', 'crystal_gallery', 'sky_ridge'])
    .default('summit_trail'),
  // Expedition mode. Optional + defaults for parties created before it.
  expeditionCoop: z.boolean().default(true),
  expeditionStages: z.number().int().min(6).max(15).default(10),
  expeditionStamina: z.number().int().min(3).max(8).default(5),
  expeditionMap: z
    .enum(['alpine', 'ember_rift', 'frost_couloir'])
    .default('alpine'),
});

export const PartySchema = z.object({
  id: z.string().uuid(),
  gymId: z.string().uuid(),
  name: z.string().min(1).max(80),
  joinCode: z.string().length(6),
  status: PartyStatusSchema,
  hostProfileId: z.string().uuid().nullable(),
  hostGuestId: z.string().uuid().nullable(),
});

export const PartyCreateSchema = z.object({
  gymId: z.string().uuid(),
  name: z.string().min(1).max(80),
  // Which climbing discipline this session is for (a gym_disciplines
  // row). Omitted by single-discipline gyms and by older clients — the
  // server then falls back to the gym's default enabled discipline.
  disciplineId: z.string().uuid().optional(),
});

export const GuestIdentitySchema = z.object({
  kind: z.literal('guest'),
  displayName: z.string().min(1).max(40),
  avgDifficultyRank: z.number().int().min(0),
});

export const AuthIdentitySchema = z.object({
  kind: z.literal('auth'),
});

export const IdentitySchema = z.discriminatedUnion('kind', [
  GuestIdentitySchema,
  AuthIdentitySchema,
]);

export const PartyJoinSchema = z.object({
  joinCode: z.string().length(6),
  identity: IdentitySchema,
});

export const SpinRequestSchema = z.object({
  partyMemberId: z.string().uuid(),
});

export const SpinCompleteSchema = z.object({
  spinId: z.string().uuid(),
  selectedRouteId: z.string().uuid(),
  betaViewed: z.boolean(),
});

export type Gym = z.infer<typeof GymSchema>;
export type Route = z.infer<typeof RouteSchema>;
export type ChallengeModifier = z.infer<typeof ChallengeModifierSchema>;
export type Party = z.infer<typeof PartySchema>;
export type GameSettingsValidated = z.infer<typeof GameSettingsSchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type PartyJoin = z.infer<typeof PartyJoinSchema>;
export type SpinRequest = z.infer<typeof SpinRequestSchema>;
export type SpinComplete = z.infer<typeof SpinCompleteSchema>;
